import type { ExpandableMenuList, HomeLink } from '@pesto/ui/components/Navigation/Navigation.types';
import type { ReactNode } from 'react';

import { useAuth } from '@pesto/features';
import { PestoWordmark } from '@pesto/ui/components/PestoWordmark';
import { ApplicationLayout } from '@pesto/ui/layouts/ApplicationLayout';
import { useTheme } from '@pesto/ui/providers/ThemeProvider';
import { CreditCard, FileUp, Home, Landmark, Users } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { CARDS_DEFAULT_HREF, LOANS_DEFAULT_HREF, USERS_DEFAULT_HREF } from '../constants/defaultListFilters';
import { getEnvBadgeConfig } from '../constants/global-env';
import { routes } from '../constants/routes';
import { useRole } from '../hooks/useRole';

export interface ApplicationLayoutProps {
  className?: string;
  children?: ReactNode;
  component?: ReactNode;
}

export const PartnerApplicationLayout = (props: ApplicationLayoutProps) => {
  const { children } = props;
  const { signOut } = useAuth();
  const { setTheme } = useTheme();
  const { isPartnerAdmin } = useRole();

  const userMenuItems: ExpandableMenuList = [
    { type: 'label', label: 'My Account' },
    { type: 'separator' },
    {
      type: 'sub',
      label: 'Theme',
      items: [
        { type: 'item', label: 'Light', onClick: () => setTheme('light') },
        { type: 'item', label: 'Dark', onClick: () => setTheme('dark') },
        { type: 'item', label: 'System', onClick: () => setTheme('system') },
      ],
    },
    { type: 'separator' },
    { type: 'item', label: 'Logout', onClick: () => signOut() },
  ];

  const menuLinks: ExpandableMenuList = [
    { type: 'item', href: routes.dashboard, label: 'Dashboard', icon: Home },
    {
      type: 'item',
      href: USERS_DEFAULT_HREF,
      label: 'Users',
      icon: Users,
    },
    { type: 'item', href: LOANS_DEFAULT_HREF, label: 'Loans', icon: Landmark },
    { type: 'item', href: CARDS_DEFAULT_HREF, label: 'Cards', icon: CreditCard },
    ...(isPartnerAdmin ? [{ type: 'item' as const, href: routes.loanData, label: 'Loan Data', icon: FileUp }] : []),
  ];

  const envBadge = getEnvBadgeConfig() ?? undefined;

  const homeLink: HomeLink = {
    name: 'Partner Control',
    href: routes.dashboard,
    icon: CreditCard,
    Logo: PestoWordmark,
    logoClassName: 'text-accent',
    subtitle: 'Partner Control',
    envBadge,
  };

  const location = useLocation();

  if (location.pathname === routes.login) {
    return <>{children}</>;
  }

  return (
    <ApplicationLayout
      desktopNavClassName="bg-sidebar"
      triggerClassName="text-background"
      userMenuItems={userMenuItems}
      menuLinks={menuLinks}
      homeLink={homeLink}
    >
      {children}
    </ApplicationLayout>
  );
};
